/**
 * The circuit in force, for everything that depends on it: the menu, the title,
 * the home button, the message list.
 *
 * It is derived from the route (see `circuits/circuits.ts`), so there is no
 * second source of truth to keep in step with the address. The one piece of
 * memory is the circuit seen last, for pages that belong to no circuit.
 */
import { computed, ref, watch } from 'vue'

import { circuitOfRoute, circuitPageRoute, resolveCircuit } from '@/circuits/circuits'
import { currentRoute, useCustomRouter } from '@/composables/useCustomRouter'
import { config } from '@/composables/useMqttConnection'
import type { ISystem } from '@/interfaces/gui'

const { navigateTo } = useCustomRouter()

const circuits = computed<ReadonlyArray<ISystem>>(() => config.value?.systems ?? [])
const lastCircuitName = ref<string>()

watch(
  [currentRoute, circuits],
  ([route, all]) => {
    const circuit = circuitOfRoute(route, all)
    if (circuit) lastCircuitName.value = circuit.name
  },
  { immediate: true },
)

const currentCircuit = computed(() =>
  resolveCircuit(currentRoute.value, circuits.value, lastCircuitName.value),
)
const isMultiCircuit = computed(() => circuits.value.length > 1)

export function useCircuit() {
  /** Opens the start screen of a circuit, which puts its menu and messages in force. */
  function switchCircuit(circuit: ISystem): void {
    lastCircuitName.value = circuit.name
    navigateTo(`/${circuit.startscreen}`)
  }

  /** The route of an app page inside the circuit in force. */
  function pageRoute(page: string): string {
    return circuitPageRoute(currentCircuit.value, page)
  }

  return {
    circuits,
    currentCircuit,
    isMultiCircuit,
    switchCircuit,
    pageRoute,
  }
}
